import { useLazyQuery } from "@apollo/client";
import { compact } from "lodash";
import { useCallback } from "react";
import {
  AskAiChatMessageFragmentFragment,
  ChatbotRoleEnum,
  GetAskAiChatSessionQueryQuery,
  GetAskAiChatSessionQueryQueryVariables,
} from "types/graphql-schema";

import { chatToolCallsVar } from "@apps/ask-ai/ask-ai-context";
import getAskAIChatSessionQuery, {
  AskAiChatMessageFragment,
} from "@apps/ask-ai/graphql/get-ask-ai-session-query";
import { createChatMessageNode } from "@apps/ask-ai/helpers";
import cache from "@cache/cache";
import usePusher from "@components/use-pusher/use-pusher";

export default function PusherNewChatMessage({
  channelName,
}: {
  channelName: string;
}) {
  const [refreshChatSession] = useLazyQuery<
    GetAskAiChatSessionQueryQuery,
    GetAskAiChatSessionQueryQueryVariables
  >(getAskAIChatSessionQuery);

  const handleNewChatMessage = useCallback(
    ({
      chat_session_id,
      new_message,
      message_id,
    }: {
      chat_session_id: number;
      new_message: {
        content: string;
        role: ChatbotRoleEnum;
        id: string;
      };
      message_id: string;
    }) => {
      const chatMessageCacheId = cache.identify({
        __typename: "ChatMessageNode",
        id: message_id,
      });
      const chatMessageCache =
        cache.readFragment<AskAiChatMessageFragmentFragment>({
          id: chatMessageCacheId,
          fragmentName: "AskAiChatMessageFragment",
          fragment: AskAiChatMessageFragment,
        });

      if (chatMessageCache) {
        const wasStreamingJSON =
          chatMessageCache.isStreaming &&
          chatMessageCache.content.trim().startsWith("{");

        // update existing message
        cache.updateFragment(
          {
            id: chatMessageCacheId,
            fragmentName: "AskAiChatMessageFragment",
            fragment: AskAiChatMessageFragment,
          },
          (data) =>
            // not make the transition smooth from streaming embeds to final embeds
            // we do NOT refresh the chat session cache when streaming
            // we refresh it all at once when calling refreshChatSession
            wasStreamingJSON
              ? data
              : {
                  ...data,
                  content: new_message.content,
                  role: new_message.role,
                  isStreaming: false,
                },
        );
      } else {
        // add new message to cached message list
        cache.updateQuery<
          GetAskAiChatSessionQueryQuery,
          GetAskAiChatSessionQueryQueryVariables
        >(
          {
            query: getAskAIChatSessionQuery,
            variables: { chatSessionId: Number(chat_session_id) },
          },
          (cachedData) => {
            const chatSession = cachedData?.chatSession;
            if (!chatSession) return null;
            return {
              ...cachedData,
              __typename: "Query",
              chatSession: {
                ...chatSession,
                updated: new Date().toISOString(),
                messages: {
                  ...chatSession.messages,
                  edges: compact([
                    ...chatSession.messages.edges,
                    {
                      __typename: "ChatMessageEdge",
                      node: createChatMessageNode({
                        content: new_message.content,
                        role: new_message.role,
                        id: new_message.id,
                        toolCalls: chatToolCallsVar()
                          .filter((tc) => tc.chatSessionId === chat_session_id)
                          .map((tc) => ({
                            __typename: "ChatToolCallNode" as const,
                            toolName: tc.toolName,
                            resultCount: tc.resultCount ?? null,
                            arguments: null,
                            output: null,
                          })),
                      }),
                    },
                  ]),
                },
              },
            } as const;
          },
        );
      }
      refreshChatSession({
        variables: { chatSessionId: chat_session_id },
      });
    },
    [refreshChatSession],
  );

  usePusher(channelName, "new_chat_message", handleNewChatMessage);

  return null;
}
